// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/system';
import {
  IconButton, InputBase, MenuItem, Select, Tooltip, Typography,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import stylesDef from './styles';
import { PROJECT_SORT_OPTIONS } from '../projectViewOptions';

interface IProjectsControlsProps {
  title: string;
  sortFilter: string;
  onSortChange: (value: string) => void;
  orderFilter: string;
  onOrderFilterChange: (value: ProjectOrderSortFilter) => void;
  // Which filter options this surface offers: the dashboard shows every one, a folder leaves
  // out the Unassigned filter because everything inside a folder is in it.
  filterOptions: { value: ProjectOrderSortFilter; label: string }[];
  onOrderFilterOpen: (() => void) | undefined;
  viewMode: ProjectViewMode;
  onViewModeChange: (mode: ProjectViewMode) => void;
}

// AUT-6432: the row above a set of projects - a title on the left, and on the right the sort
// and filter dropdowns with the list/cards switch. Presentational on purpose: the dashboard
// drives it from the store, a folder page from its own state, and neither can make the
// controls look or behave differently from the other.
export const ProjectsControls = ({
  title, sortFilter, onSortChange, orderFilter, onOrderFilterChange,
  filterOptions, onOrderFilterOpen, viewMode, onViewModeChange,
}: IProjectsControlsProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  // Both dropdowns open beneath their control with right edges aligned, so a menu never
  // covers the label that opened it. Stable across renders so MUI is not handed a new object.
  const menuProps = useMemo(() => ({
    anchorOrigin: { vertical: 'bottom' as const, horizontal: 'right' as const },
    transformOrigin: { vertical: 'top' as const, horizontal: 'right' as const },
    PaperProps: { sx: styles.menuPaper },
  }), [theme]);

  return (
    <Box sx={styles.subheader}>
      <Typography variant="h5" sx={styles.title}>{title}</Typography>
      <Box sx={styles.controls}>
        <Select
          inputProps={{ name: 'sort', id: 'sort' }}
          input={<InputBase />}
          value={sortFilter}
          sx={styles.dropdown}
          MenuProps={menuProps}
          onChange={(ev) => onSortChange(ev.target.value)}
        >
          {PROJECT_SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={styles.menuItem}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <Select
          inputProps={{ name: 'filterby', id: 'filterby' }}
          input={<InputBase />}
          value={orderFilter}
          sx={styles.dropdown}
          MenuProps={menuProps}
          onOpen={onOrderFilterOpen}
          onChange={(ev) => {
            // Every item's value comes from filterOptions, so the widened string MUI hands
            // back is always one of the union's members.
            onOrderFilterChange(ev.target.value as ProjectOrderSortFilter);
          }}
        >
          {filterOptions.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={styles.menuItem}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <Box sx={styles.divider} aria-hidden="true" />
        <Box sx={styles.viewSwitch}>
          <Tooltip title="View Projects as List" arrow>
            <IconButton
              aria-label="View Projects as List"
              aria-pressed={viewMode === 'list'}
              sx={[styles.viewSwitchButton, viewMode === 'list' && styles.viewSwitchButtonActive]}
              onClick={() => onViewModeChange('list')}
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Projects as Cards" arrow>
            <IconButton
              aria-label="View Projects as Cards"
              aria-pressed={viewMode === 'cards'}
              sx={[styles.viewSwitchButton, viewMode === 'cards' && styles.viewSwitchButtonActive]}
              onClick={() => onViewModeChange('cards')}
            >
              <GridViewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectsControls;
